import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Gauge, Euro, Database, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  "detecteur_auto": "Detecteur automatique",
  "deep:planner": "Analyse renforcee — plan",
  "deep:recherche_web": "Analyse renforcee — recherches web",
  "deep:reponse_expert": "Analyse renforcee — reponse",
  "deep:cache_hit": "Analyse renforcee — servie par le cache",
  "reunion:expert": "Reunion d'equipage — experts",
  "reunion:verdict": "Reunion d'equipage — verdict",
  "pdf:extraction": "Rapports PDF — lecture des donnees",
};

const featureLabel = (f) =>
  FEATURE_LABELS[f] || (f?.startsWith("chat:") ? `Chat ${f.split(":")[1]}` : f);

const eur = (v) => (v >= 0.01 ? `${v.toFixed(2)} EUR` : v > 0 ? "< 0,01 EUR" : "0 EUR");

/** Carte admin : cout IA REEL mesure + plafond des analyses renforcees. */
export const DeepUsageCard = () => {
  const [quota, setQuota] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limit, setLimit] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const token = localStorage.getItem("odys_token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [q, u] = await Promise.all([
        axios.get(`${API}/api/admin/deep-usage`, { headers }),
        axios.get(`${API}/api/admin/llm-usage?days=30`, { headers }),
      ]);
      setQuota(q.data);
      setUsage(u.data);
      setLimit(String(q.data.deep_limit));
    } catch (e) {
      console.error("usage load failed", e);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("odys_token");
      await axios.put(`${API}/api/admin/deep-usage/limit`, { limit: Number(limit) }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Plafond mensuel mis a jour");
      await load();
    } catch (e) {
      toast.error("Echec de la mise a jour");
    } finally {
      setSaving(false);
    }
  };

  if (!quota) return null;
  const pct = quota.deep_limit ? Math.min(100, Math.round((quota.deep_runs_auto / quota.deep_limit) * 100)) : 0;
  const features = (usage?.by_feature || []).filter((f) => f.calls > 0).slice(0, 8);
  const maxCost = features.length ? Math.max(...features.map((f) => f.cost_eur)) || 1 : 1;

  return (
    <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="deep-usage-card">
      <CardContent className="p-5 space-y-5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#B5894A]" strokeWidth={1.75} />
          <h3 className="text-sm font-semibold text-[#1F2937]">
            Ou part ton credit IA — mesure reel sur 30 jours
          </h3>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-2xl font-bold text-[#B5894A]" data-testid="llm-total-cost">
              {eur(usage?.total_cost_eur || 0)}
            </p>
            <p className="text-xs text-[#6B7280] flex items-center gap-1">
              <Euro className="w-3 h-3" /> Cout IA mesure (30 j)
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1F2937]">{eur(usage?.cost_per_student_eur || 0)}</p>
            <p className="text-xs text-[#6B7280]">Par eleve actif</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1F2937]" data-testid="deep-runs-count">
              {quota.deep_runs_auto}
              <span className="text-sm font-normal text-[#6B7280]"> / {quota.deep_limit}</span>
            </p>
            <p className="text-xs text-[#6B7280] flex items-center gap-1">
              <Gauge className="w-3 h-3" /> Reflexions automatiques ce mois
            </p>
            <p className="text-[11px] text-[#9CA3AF]">
              {quota.analysis_runs ?? 0} analyses renforcees · {quota.collegial_runs ?? 0} conseils collegiaux
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1F2937]">{quota.cache_hit_rate}%</p>
            <p className="text-xs text-[#6B7280] flex items-center gap-1">
              <Database className="w-3 h-3" /> Recherches servies par le cache
            </p>
          </div>
        </div>

        {features.length > 0 && (
          <div className="space-y-2" data-testid="llm-usage-breakdown">
            {features.map((f) => (
              <div key={f.feature} className="flex items-center gap-3">
                <span className="text-xs text-[#4B5563] w-56 shrink-0 truncate">
                  {featureLabel(f.feature)}
                </span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.max(2, (f.cost_eur / maxCost) * 100)}%`, background: "#B5894A" }}
                  />
                </div>
                <span className="text-xs font-semibold text-[#1F2937] w-24 text-right">{eur(f.cost_eur)}</span>
                <span className="text-[11px] text-[#9CA3AF] w-16 text-right">
                  {f.calls} appel{f.calls > 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="pt-1 border-t border-slate-100 space-y-3">
          <div className="h-1.5 rounded-full overflow-hidden bg-slate-100">
            <div
              className="h-full transition-all"
              style={{ width: `${Math.max(2, pct)}%`, background: pct > 85 ? "#C2492F" : "#B5894A" }}
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 max-w-[180px]">
              <label className="text-xs text-[#6B7280] block mb-1 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Plafond mensuel
              </label>
              <Input
                value={limit}
                onChange={(e) => setLimit(e.target.value.replace(/\D/g, ""))}
                data-testid="deep-limit-input"
                className="h-9"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving || !limit}
              data-testid="deep-limit-save-btn"
              className="h-9 bg-[#16233B] hover:bg-[#16233B]/90"
            >
              Enregistrer
            </Button>
          </div>
          <p className="text-[11px] text-[#6B7280]">
            Au-dela du plafond, les experts arretent de declencher l'analyse renforcee automatiquement.
            Aucun impact visible pour les eleves. Les couts sont calcules appel par appel a partir des
            tarifs Anthropic (Haiku 4.5, Sonnet 4.6, Opus 4.8) et des frais de recherche web.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
